// Sound cues (issue 1162). The DM ties one of their uploaded tracks (issue 1161) to something that happens at the
// table — a token entering a painted zone, a token's turn starting, an attack that hits, an encounter starting or
// ending — and the server plays it when that happens (`cueFiring.ts` says which cues a change fires).
//
// The cues are the DM's prep and the DM's alone: held on the table's own log beside the sound, never in a projected
// state, and their events project to nothing for a player or a watcher. What a player learns of a cue is only the
// ordinary `audio.play` it fires, the same sound #1161 sends whenever the DM plays one by hand.
//
// A cue may also SAY A LINE (qa-runs#1168): one of its lines, at random, by its speaker, as an ordinary NPC-dialog
// `dialog.say` (dialog.ts) — to the scene, or only to the players whose token set it off; once per session (the
// default), every time, or once ever. A token's PREPARED LINES are held the same way, as a cue with the `prepared`
// trigger that nothing fires: the DM says one by hand from the Dialogue panel. Both are the DM's prep, never projected.
import { DIALOG_KINDS, DIALOG_LIMITS, cleanLineText, cleanSpeakerName, isDialogBody, parseDialogBody } from './dialog.js';
/** The triggers the table fires (the Cues desk offers these; `prepared` is held apart). */
export const CUE_TRIGGERS = ['area_enter', 'token_acts', 'attack_hit', 'encounter_start', 'encounter_end'];
export const TRIGGER_LIMITS = { name: 60, fight: 50 };
/** qa-runs#1445 (trillhc, B): a table holds 250 cues and triggers together. */
export const CUE_LIMITS = { cues: 250, lines: DIALOG_LIMITS.prepared };
export const NOTHING_TO_DO = 'A cue plays a sound, says a line, stops movement, or more than one of those.';
export const CUES_FULL = 'This table holds as many sound cues as it can. Remove one first.';
export const CUE_GONE = 'That sound cue is gone.';
export const CUE_KINDS = ['cue.set', 'cue.remove', ...DIALOG_KINDS];
export const isCueBody = (b) => CUE_KINDS.includes(b.kind);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const exactly = (o, keys) => Object.keys(o).every((k) => keys.includes(k));
export function parseCueTrigger(v) {
    if (!isObj(v))
        return null;
    switch (v.on) {
        case 'area_enter': return exactly(v, ['on', 'zoneId']) && isUuid(v.zoneId) ? { on: 'area_enter', zoneId: v.zoneId } : null;
        case 'token_acts': return exactly(v, ['on', 'objectId']) && isUuid(v.objectId) ? { on: 'token_acts', objectId: v.objectId } : null;
        case 'attack_hit':
            if (!exactly(v, ['on', 'objectId']))
                return null;
            if (v.objectId === undefined)
                return { on: 'attack_hit' };
            return isUuid(v.objectId) ? { on: 'attack_hit', objectId: v.objectId } : null;
        case 'encounter_start':
        case 'encounter_end': return exactly(v, ['on']) ? { on: v.on } : null;
        case 'prepared': return exactly(v, ['on', 'objectId']) && isUuid(v.objectId) ? { on: 'prepared', objectId: v.objectId } : null;
        default: return null;
    }
}
export function parseCueSay(v) {
    if (!isObj(v) || !exactly(v, ['speaker', 'name', 'lines', 'to', 'every']))
        return null;
    if (!Array.isArray(v.lines) || !v.lines.length || v.lines.length > CUE_LIMITS.lines)
        return null;
    const lines = v.lines.map(cleanLineText);
    if (lines.some((l) => l === null))
        return null;
    if (v.speaker !== undefined && !isUuid(v.speaker))
        return null;
    const name = cleanSpeakerName(v.name);
    if (name === null)
        return null;
    if (v.to !== undefined && v.to !== 'owners')
        return null;
    if (v.every !== undefined && v.every !== 'always' && v.every !== 'ever')
        return null;
    return { lines: lines, ...(v.speaker ? { speaker: v.speaker } : {}), ...(name ? { name } : {}), ...(v.to ? { to: 'owners' } : {}), ...(v.every ? { every: v.every } : {}) };
}
export function parseCue(v) {
    if (!isObj(v) || !isUuid(v.id) || (v.trackId !== undefined && !isUuid(v.trackId)) || (v.channel !== 'effect' && v.channel !== 'music'))
        return null;
    if (v.by !== undefined && !isUuid(v.by))
        return null;
    const trigger = parseCueTrigger(v.trigger);
    if (!trigger)
        return null;
    const say = v.say === undefined ? undefined : parseCueSay(v.say);
    if (say === null)
        return null;
    if (v.stop !== undefined && (v.stop !== 'token' && v.stop !== 'all' || trigger.on !== 'area_enter'))
        return null;
    if (v.who !== undefined && v.who !== 'players' && !isUuid(v.who))
        return null;
    if (v.off !== undefined && v.off !== true)
        return null;
    if (v.every !== undefined && v.every !== 'always' && v.every !== 'ever')
        return null;
    if (v.name !== undefined && (typeof v.name !== 'string' || !v.name.trim() || v.name.length > TRIGGER_LIMITS.name))
        return null;
    if (v.fight !== undefined && !(Array.isArray(v.fight) && v.fight.length <= TRIGGER_LIMITS.fight && v.fight.every(isUuid) && new Set(v.fight).size === v.fight.length))
        return null;
    if (v.trap !== undefined && !isUuid(v.trap))
        return null;
    if (v.auto !== undefined && v.auto !== true)
        return null;
    if ((v.fight !== undefined || v.trap !== undefined) && trigger.on !== 'area_enter')
        return null;
    if (!v.trackId && !say && !v.stop && !v.fight && !v.trap)
        return null;
    // Prepared lines play nothing and speak as their own token.
    if (trigger.on === 'prepared' && (v.trackId || !say || (say.speaker && say.speaker !== trigger.objectId)))
        return null;
    return {
        id: v.id, trigger, channel: v.channel,
        ...(v.trackId ? { trackId: v.trackId } : {}),
        ...(v.by !== undefined ? { by: v.by } : {}),
        ...(say ? { say: trigger.on === 'prepared' ? { ...say, speaker: trigger.objectId } : say } : {}),
        ...(v.stop ? { stop: v.stop } : {}), ...(v.who ? { who: v.who } : {}),
        ...(v.off ? { off: true } : {}), ...(v.every ? { every: v.every } : {}),
        ...(v.name ? { name: v.name.trim() } : {}), ...(v.fight ? { fight: [...v.fight] } : {}),
        ...(v.trap ? { trap: v.trap } : {}), ...(v.auto ? { auto: true } : {}),
    };
}
export function parseCueBody(b) {
    if (b.kind === 'cue.set') {
        const cue = parseCue(b.cue);
        return cue ? { kind: 'cue.set', cue } : null;
    }
    if (b.kind === 'cue.remove')
        return isUuid(b.id) ? { kind: 'cue.remove', id: b.id } : null;
    return parseDialogBody(b);
}
/** Why a cue change cannot stand, in words, or null: the table full, or the cue already gone. */
export function cueRefusal(cues, body) {
    if (isDialogBody(body))
        return null;
    if (body.kind === 'cue.set')
        return cues.some((c) => c.id === body.cue.id) || cues.length < CUE_LIMITS.cues ? null : CUES_FULL;
    return cues.some((c) => c.id === body.id) ? null : CUE_GONE;
}
const copyCue = (c) => ({ ...c, trigger: { ...c.trigger }, ...(c.say ? { say: { ...c.say, lines: [...c.say.lines] } } : {}), ...(c.fight ? { fight: [...c.fight] } : {}) });
/** A token's prepared lines (qa-runs#1168): the cue holding them, or undefined. */
export const preparedOf = (cues, objectId) => (cues ?? []).find((c) => c.trigger.on === 'prepared' && c.trigger.objectId === objectId);
export function reduceCues(cues, body) {
    // A line said or deleted changes no cue: it lives on the log alone.
    if (isDialogBody(body))
        return [...(cues ?? [])];
    const cur = (cues ?? []).map(copyCue);
    if (body.kind === 'cue.remove')
        return cur.filter((c) => c.id !== body.id);
    const i = cur.findIndex((c) => c.id === body.cue.id);
    if (i >= 0) {
        cur[i] = copyCue(body.cue);
        return cur;
    }
    return [...cur, copyCue(body.cue)].slice(0, CUE_LIMITS.cues);
}
/** A stored state's cues read through; anything unreadable dropped. */
export function normaliseCues(v) {
    if (!Array.isArray(v))
        return [];
    return v.map(parseCue).filter((c) => c !== null).slice(0, CUE_LIMITS.cues);
}
//# sourceMappingURL=cues.js.map